import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Shuffle, BookOpen, Sparkles, Info, RotateCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import TarotCardDetail from './TarotCardDetail';
import DOMPurify from 'dompurify';

const SAVED_SPREAD_KEY = 'tarot_spread_in_progress';

interface TarotSpreadJournalProps {
  onClose: () => void;
}

const SPREADS = [
  {
    name: 'Past-Present-Future',
    positions: ['Past', 'Present', 'Future'],
    description: 'Quick insights on your timeline',
    layout: 'linear',
    bestFor: 'Quick guidance and understanding how past events are influencing your present and shaping your future',
    goodQuestions: [
      'How is my past affecting my current situation?',
      'What do I need to understand about where I am now?',
      'What energy is coming toward me?'
    ]
  },
  {
    name: 'Celtic Cross',
    positions: [
      'Present Situation', 
      'Challenge/Crossing', 
      'Distant Past', 
      'Recent Past', 
      'Best Outcome', 
      'Immediate Future', 
      'Your Approach', 
      'External Influences', 
      'Hopes & Fears', 
      'Final Outcome'
    ],
    description: 'Deep dive into complex situations',
    layout: 'celtic-cross',
    bestFor: 'Complex situations where you need comprehensive insight into all the factors at play - past influences, present challenges, future possibilities, and your role in the outcome',
    goodQuestions: [
      'What do I need to know about [complex situation]?',
      'How can I navigate this major life transition?',
      'What is blocking me and how can I move forward?'
    ]
  },
  {
    name: 'Relationship Spread',
    positions: ['You', 'Them', 'Connection', 'Obstacles', 'Potential'],
    description: 'Understand relationship dynamics',
    layout: 'linear',
    bestFor: 'Any relationship - romantic, friendship, family, or work. Reveals what each person brings, the energy between you, challenges, and potential growth',
    goodQuestions: [
      'What do I need to understand about this relationship?',
      'How can we improve our connection?',
      'What is the highest potential for this relationship?'
    ]
  }
];

export default function TarotSpreadJournal({ onClose }: TarotSpreadJournalProps) {
  const [selectedSpread, setSelectedSpread] = useState(SPREADS[0]);
  const [drawnCards, setDrawnCards] = useState<any[]>([]);
  const [question, setQuestion] = useState('');
  const [interpretation, setInterpretation] = useState('');
  const [reflection, setReflection] = useState('');
  const [symbolicInsights, setSymbolicInsights] = useState('');
  const [aiReading, setAiReading] = useState('');
  const [isGeneratingReading, setIsGeneratingReading] = useState(false);
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [showDirections, setShowDirections] = useState(true);
  const { toast } = useToast();

  // Restore saved spread on mount
  useEffect(() => {
    const saved = localStorage.getItem(SAVED_SPREAD_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const spread = SPREADS.find(s => s.name === parsed.spreadName) || SPREADS[0];
        setSelectedSpread(spread);
        setDrawnCards(parsed.drawnCards || []);
        setQuestion(parsed.question || '');
        setInterpretation(parsed.interpretation || '');
        setReflection(parsed.reflection || '');
        setSymbolicInsights(parsed.symbolicInsights || '');
        setAiReading(parsed.aiReading || '');
        if (parsed.drawnCards?.length > 0) {
          setShowDirections(false);
          toast({ 
            title: 'Spread Restored', 
            description: 'Your previous reading has been restored. Continue where you left off!'
          });
        }
      } catch (e) {
        console.error('Error restoring saved spread:', e);
      }
    }
  }, []);

  // Auto-save spread progress
  useEffect(() => {
    if (drawnCards.length > 0) {
      const toSave = {
        spreadName: selectedSpread.name,
        drawnCards,
        question,
        interpretation,
        reflection,
        symbolicInsights,
        aiReading,
        timestamp: Date.now()
      };
      localStorage.setItem(SAVED_SPREAD_KEY, JSON.stringify(toSave));
    }
  }, [drawnCards, question, interpretation, reflection, symbolicInsights, aiReading, selectedSpread]);

  const drawCards = async () => {
    if (!question.trim()) {
      toast({ title: 'Please enter a question first', variant: 'destructive' });
      return;
    }

    const { data: allCards } = await supabase
      .from('tarot_cards')
      .select('card_name, card_number, suit, image_url, upright_meaning, reversed_meaning, keywords, story, imagery_explanation')
      .eq('is_published', true);

    if (!allCards || allCards.length === 0) {
      toast({ title: 'No cards available', variant: 'destructive' });
      return;
    }

    const shuffled = [...allCards].sort(() => Math.random() - 0.5);
    const drawn = shuffled.slice(0, selectedSpread.positions.length).map((card, index) => ({
      ...card,
      name: card.card_name,
      number: card.card_number,
      imageUrl: card.image_url,
      uprightMeaning: card.upright_meaning,
      reversedMeaning: card.reversed_meaning,
      position: selectedSpread.positions[index],
      positionNumber: index + 1,
      isReversed: Math.random() > 0.5
    }));
    setDrawnCards(drawn);
    setAiReading('');
    setInterpretation('');
    setReflection('');
    setSymbolicInsights('');
    setShowDirections(false);
  };

  const generateAiReading = async () => {
    if (drawnCards.length === 0) {
      toast({ title: 'Draw cards first', variant: 'destructive' });
      return;
    }

    if (!interpretation.trim()) {
      toast({ 
        title: 'Write Your Interpretation First!', 
        description: "Share what you see in the cards first, then get Tarot Teacher's insights.",
        variant: 'destructive' 
      });
      return;
    }

    setIsGeneratingReading(true);
    setAiReading('');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({ title: 'Please log in', variant: 'destructive' });
        return;
      }

      // Get user's name from profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('user_id', user.id)
        .single();

      const userName = profile?.display_name || 'seeker';

      // Prepare card data with positions
      const cardData = drawnCards.map((card, idx) => ({
        position: selectedSpread.positions[idx],
        positionNumber: idx + 1,
        name: card.card_name,
        upright_meaning: card.upright_meaning,
        reversed_meaning: card.reversed_meaning,
        keywords: card.keywords
      }));

      const hasInterpretation = interpretation.trim().length > 0;
      
      const prompt = `You are Tarot Teacher. ${userName} did a ${selectedSpread.name} reading.

Question: "${question}"
${hasInterpretation ? `Their interpretation: "${interpretation}"` : ''}

Cards:
${cardData.map(c => `${c.position}: ${c.name} (${c.keywords?.slice(0, 3).join(', ')})`).join('\n')}

Structure response:
## Hello ${userName}! 👋
[Warm greeting]

${hasInterpretation ? `## Your Interpretation 📝
[Acknowledge 2-3 key points they saw]

` : ''}## Tarot Teacher's Insights ✨
${cardData.map(c => `### ${c.position} - ${c.name}
[Explain position meaning, card message, connection to their question]`).join('\n\n')}

## Personal Summary 🌟
[3-4 sentences tying cards together, answering question, offering encouragement]`;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: 'Authentication required', variant: 'destructive' });
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://jbdsdzmyqkfbqijnoqmo.supabase.co';
      const response = await fetch(
        `${supabaseUrl}/functions/v1/guidance-interpreter`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: [{ role: 'user', content: prompt }]
          })
        }
      );

      if (!response.ok) {
        if (response.status === 402) {
          toast({ 
            title: 'Insufficient Credits', 
            description: 'You need credits to generate AI readings. Visit the Credits page to purchase more.',
            variant: 'destructive' 
          });
          return;
        }
        
        if (response.status === 429) {
          toast({ 
            title: 'Too many requests', 
            description: 'Please wait a moment before trying again.',
            variant: 'destructive' 
          });
          return;
        }
        
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to generate reading');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let textBuffer = '';

      if (reader) {
        let streamDone = false;

        while (!streamDone) {
          const { done, value } = await reader.read();
          if (done) break;

          textBuffer += decoder.decode(value, { stream: true });
          let newlineIndex: number;

          while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
            let line = textBuffer.slice(0, newlineIndex);
            textBuffer = textBuffer.slice(newlineIndex + 1);

            if (line.endsWith('\r')) line = line.slice(0, -1);
            if (line.startsWith(':') || line.trim() === '') continue;
            if (!line.startsWith('data: ')) continue;

            const payload = line.slice(6).trim();
            if (payload === '[DONE]') { streamDone = true; break; }

            try {
              const parsed = JSON.parse(payload);
              if (parsed.done === true) { streamDone = true; break; }
              const content =
                parsed.choices?.[0]?.delta?.content ??
                parsed.text ??
                parsed.delta?.text ??
                parsed.content ??
                null;
              if (content) {
                accumulatedText += content;
                setAiReading(accumulatedText);
              }
            } catch {
              console.warn('[Tarot SSE] parse skip:', payload.slice(0, 120));
            }
          }
        }
      }

      if (accumulatedText) {
        toast({ title: 'Reading generated!' });
      }
    } catch (error) {
      console.error('Error generating reading:', error);
      toast({ 
        title: 'Error generating reading', 
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive' 
      });
    } finally {
      setIsGeneratingReading(false);
    }
  };

  const clearSavedSpread = () => {
    localStorage.removeItem(SAVED_SPREAD_KEY);
  };

  const startFreshSpread = () => {
    clearSavedSpread();
    setDrawnCards([]);
    setQuestion('');
    setInterpretation('');
    setReflection('');
    setSymbolicInsights('');
    setAiReading('');
    setShowDirections(true);
    toast({ title: 'Starting fresh spread' });
  };

  const saveJournalEntry = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({ title: 'Please log in to save', variant: 'destructive' });
      return;
    }

    if (drawnCards.length === 0) {
      toast({ title: 'Draw cards first', variant: 'destructive' });
      return;
    }

    const { error } = await supabase.from('tarot_journal_entries').insert({
      user_id: user.id,
      spread_type: selectedSpread.name,
      question,
      cards_drawn: drawnCards.map((card, idx) => ({
        position: selectedSpread.positions[idx],
        card_name: card.card_name,
        card_number: card.card_number,
        suit: card.suit
      })),
      interpretation,
      reflection,
      symbolic_insights: symbolicInsights,
      ai_reading: aiReading
    } as any);

    if (error) {
      toast({ title: 'Error saving entry', variant: 'destructive' });
    } else {
      clearSavedSpread();
      toast({ title: 'Journal entry saved!' });
      onClose();
    }
  };

  const renderCelticCross = () => {
    if (drawnCards.length < 10) return null;
    
    return (
      <div className="w-full max-w-5xl mx-auto my-4 md:my-8 p-3 md:p-6 bg-card rounded-xl border-2 border-primary/20">
        <p className="text-center text-xs md:text-sm text-muted-foreground mb-3 md:mb-4">Click any card to view its full meaning</p>
        
        {/* Desktop layout */}
        <div className="hidden md:grid grid-cols-12 gap-3" style={{ minHeight: '480px' }}>
          {/* Left column - Cross formation */}
          <div className="col-span-7 relative">
            {/* Position 5: Above - Best Outcome */}
            <div className="absolute left-1/2 top-4 transform -translate-x-1/2">
              <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all" onClick={() => setSelectedCard(drawnCards[4])}>
                {renderCard(drawnCards[4], 4)}
              </div>
            </div>
            
            {/* Position 4: Left - Recent Past */}
            <div className="absolute left-8 top-1/2 transform -translate-y-1/2">
              <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all" onClick={() => setSelectedCard(drawnCards[3])}>
                {renderCard(drawnCards[3], 3)}
              </div>
            </div>
            
            {/* Position 1: Center - Present Situation */}
            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
              <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all" onClick={() => setSelectedCard(drawnCards[0])}>
                {renderCard(drawnCards[0], 0)}
              </div>
            </div>
            
            {/* Position 2: Crossing - Challenge */}
            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-90 z-20">
              <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all" onClick={() => setSelectedCard(drawnCards[1])}>
                {renderCard(drawnCards[1], 1)}
              </div>
            </div>
            
            {/* Position 6: Right - Immediate Future */}
            <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
              <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all" onClick={() => setSelectedCard(drawnCards[5])}>
                {renderCard(drawnCards[5], 5)}
              </div>
            </div>
            
            {/* Position 3: Below - Distant Past */}
            <div className="absolute left-1/2 bottom-4 transform -translate-x-1/2">
              <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all" onClick={() => setSelectedCard(drawnCards[2])}>
                {renderCard(drawnCards[2], 2)}
              </div>
            </div>
          </div>
          
          {/* Right column - Staff positions */}
          <div className="col-span-5 flex flex-col gap-3 py-4 pl-4">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setSelectedCard(drawnCards[6])}>
              {renderStaffCard(drawnCards[6], 6)}
            </div>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setSelectedCard(drawnCards[7])}>
              {renderStaffCard(drawnCards[7], 7)}
            </div>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setSelectedCard(drawnCards[8])}>
              {renderStaffCard(drawnCards[8], 8)}
            </div>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setSelectedCard(drawnCards[9])}>
              {renderStaffCard(drawnCards[9], 9)}
            </div>
          </div>
        </div>

        {/* Mobile layout - vertical stack */}
        <div className="md:hidden flex flex-col gap-4">
          {/* Cross formation */}
          <div className="flex flex-col items-center gap-3">
            <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all active:ring-primary" onClick={() => setSelectedCard(drawnCards[4])}>
              {renderMobileCard(drawnCards[4], 4)}
            </div>
            <div className="flex items-center justify-center gap-3">
              <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all active:ring-primary" onClick={() => setSelectedCard(drawnCards[3])}>
                {renderMobileCard(drawnCards[3], 3)}
              </div>
              <div className="relative">
                <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all active:ring-primary" onClick={() => setSelectedCard(drawnCards[0])}>
                  {renderMobileCard(drawnCards[0], 0)}
                </div>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="rotate-90 cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all pointer-events-auto active:ring-primary" onClick={() => setSelectedCard(drawnCards[1])}>
                    {renderMobileCard(drawnCards[1], 1)}
                  </div>
                </div>
              </div>
              <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all active:ring-primary" onClick={() => setSelectedCard(drawnCards[5])}>
                {renderMobileCard(drawnCards[5], 5)}
              </div>
            </div>
            <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all active:ring-primary" onClick={() => setSelectedCard(drawnCards[2])}>
              {renderMobileCard(drawnCards[2], 2)}
            </div>
          </div>

          {/* Staff positions */}
          <div className="flex flex-col gap-3 pt-4 border-t border-primary/20">
            {[6, 7, 8, 9].map((idx) => (
              <div key={idx} className="flex items-center gap-3 cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all p-2 active:ring-primary" onClick={() => setSelectedCard(drawnCards[idx])}>
                <div className="w-16 h-24 rounded-lg overflow-hidden bg-card flex items-center justify-center shadow-lg border-2 border-primary/30">
                  {drawnCards[idx].image_url ? (
                    <img src={drawnCards[idx].image_url} alt={drawnCards[idx].card_name} className="w-full h-full object-contain" crossOrigin="anonymous" />
                  ) : (
                    <div className="flex flex-col items-center justify-center p-1">
                      <div className="text-sm font-bold text-primary">{drawnCards[idx].card_number}</div>
                      <div className="text-[8px] font-semibold text-center line-clamp-2">{drawnCards[idx].card_name}</div>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <Badge variant="outline" className="text-xs mb-1">{idx + 1}</Badge>
                  <p className="text-xs font-medium text-muted-foreground">{selectedSpread.positions[idx]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderPastPresentFuture = () => {
    if (drawnCards.length < 3) return null;
    
    return (
      <div className="w-full max-w-4xl mx-auto my-4 md:my-8 p-3 md:p-6 bg-card rounded-xl border-2 border-primary/20">
        <p className="text-center text-xs md:text-sm text-muted-foreground mb-4 md:mb-6">Click any card to view its full meaning</p>
        
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
          {/* Past */}
          <div className="flex flex-col items-center">
            <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all active:ring-primary" onClick={() => setSelectedCard(drawnCards[0])}>
              {renderLinearCard(drawnCards[0], 0)}
            </div>
          </div>
          
          {/* Present - Center of screen */}
          <div className="flex flex-col items-center">
            <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all active:ring-primary" onClick={() => setSelectedCard(drawnCards[1])}>
              {renderLinearCard(drawnCards[1], 1)}
            </div>
          </div>
          
          {/* Future */}
          <div className="flex flex-col items-center">
            <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all active:ring-primary" onClick={() => setSelectedCard(drawnCards[2])}>
              {renderLinearCard(drawnCards[2], 2)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRelationshipSpread = () => {
    if (drawnCards.length < 5) return null;
    
    return (
      <div className="w-full max-w-5xl mx-auto my-4 md:my-8 p-3 md:p-6 bg-card rounded-xl border-2 border-primary/20">
        <p className="text-center text-xs md:text-sm text-muted-foreground mb-4 md:mb-6">Click any card to view its full meaning</p>
        
        <div className="flex items-center justify-center gap-3 md:gap-6 flex-wrap">
          {drawnCards.map((card, idx) => (
            <div key={idx} className="flex flex-col items-center">
              <div className="cursor-pointer ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-lg hover:ring-primary transition-all active:ring-primary" onClick={() => setSelectedCard(card)}>
                {renderLinearCard(card, idx)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCard = (card: any, idx: number) => (
    <div className="space-y-1">
      <div className="w-24 h-36 rounded-lg overflow-hidden bg-card flex flex-col items-center justify-center shadow-lg border-2 border-primary/30 hover:border-primary transition-all relative">
        {card.image_url ? (
          <img 
            src={card.image_url} 
            alt={card.card_name} 
            className="w-full h-full object-contain"
            crossOrigin="anonymous"
            onError={(e) => {
              console.error('Card image failed:', card.image_url, card);
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling;
              if (fallback) (fallback as HTMLElement).style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className="absolute inset-0 flex flex-col items-center justify-center p-2 text-center bg-gradient-to-br from-primary/10 to-accent/10"
          style={{ display: card.image_url ? 'none' : 'flex' }}
        >
          <div className="text-lg font-bold text-primary mb-1">
            {card.card_number}
          </div>
          <div className="text-[10px] font-semibold line-clamp-3">
            {card.card_name}
          </div>
        </div>
      </div>
      <div className="text-center">
        <Badge variant="outline" className="text-xs mb-1">{idx + 1}</Badge>
        <p className="text-xs font-medium text-muted-foreground line-clamp-2 leading-tight">
          {selectedSpread.positions[idx]}
        </p>
      </div>
    </div>
  );

  const renderLinearCard = (card: any, idx: number) => (
    <div className="space-y-2">
      <div className="w-24 h-36 md:w-28 md:h-40 rounded-lg overflow-hidden bg-card flex flex-col items-center justify-center shadow-lg border-2 border-primary/30 hover:border-primary transition-all relative">
        {card.image_url ? (
          <img 
            src={card.image_url} 
            alt={card.card_name} 
            className="w-full h-full object-contain"
            crossOrigin="anonymous"
            onError={(e) => {
              console.error('Card image failed:', card.image_url, card);
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling;
              if (fallback) (fallback as HTMLElement).style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className="absolute inset-0 flex flex-col items-center justify-center p-2 text-center bg-gradient-to-br from-primary/10 to-accent/10"
          style={{ display: card.image_url ? 'none' : 'flex' }}
        >
          <div className="text-lg font-bold text-primary mb-1">
            {card.card_number}
          </div>
          <div className="text-xs font-semibold line-clamp-3">
            {card.card_name}
          </div>
        </div>
      </div>
      <div className="text-center space-y-1">
        <Badge variant="outline" className="text-xs">{idx + 1}</Badge>
        <p className="text-xs font-medium text-muted-foreground leading-tight">
          {selectedSpread.positions[idx]}
        </p>
      </div>
    </div>
  );

  const renderMobileCard = (card: any, idx: number) => (
    <div className="space-y-1">
      <div className="w-20 h-32 rounded-lg overflow-hidden bg-card flex flex-col items-center justify-center shadow-lg border-2 border-primary/30 relative">
        {card.image_url ? (
          <img 
            src={card.image_url} 
            alt={card.card_name} 
            className="w-full h-full object-contain"
            crossOrigin="anonymous"
            onError={(e) => {
              console.error('Card image failed:', card.image_url, card);
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling;
              if (fallback) (fallback as HTMLElement).style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className="absolute inset-0 flex flex-col items-center justify-center p-1 text-center bg-gradient-to-br from-primary/10 to-accent/10"
          style={{ display: card.image_url ? 'none' : 'flex' }}
        >
          <div className="text-sm font-bold text-primary mb-1">
            {card.card_number}
          </div>
          <div className="text-[8px] font-semibold line-clamp-3">
            {card.card_name}
          </div>
        </div>
      </div>
      <div className="text-center">
        <Badge variant="outline" className="text-[10px] mb-1">{idx + 1}</Badge>
        <p className="text-[10px] font-medium text-muted-foreground line-clamp-2 leading-tight">
          {selectedSpread.positions[idx]}
        </p>
      </div>
    </div>
  );

  const renderStaffCard = (card: any, idx: number) => (
    <>
      <div className="w-24 h-36 rounded-lg overflow-hidden bg-card flex flex-col items-center justify-center shadow-lg border-2 border-primary/30 hover:border-primary transition-all relative">
        {card.image_url ? (
          <img 
            src={card.image_url} 
            alt={card.card_name} 
            className="w-full h-full object-contain"
            crossOrigin="anonymous"
            onError={(e) => {
              console.error('Card image failed:', card.image_url, card);
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling;
              if (fallback) (fallback as HTMLElement).style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className="absolute inset-0 flex flex-col items-center justify-center p-2 text-center bg-gradient-to-br from-primary/10 to-accent/10"
          style={{ display: card.image_url ? 'none' : 'flex' }}
        >
          <div className="text-lg font-bold text-primary mb-1">
            {card.card_number}
          </div>
          <div className="text-[10px] font-semibold line-clamp-3">
            {card.card_name}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-start justify-center space-y-1">
        <Badge variant="outline" className="text-xs">{idx + 1}</Badge>
        <p className="text-xs font-medium text-muted-foreground leading-tight">
          {selectedSpread.positions[idx]}
        </p>
      </div>
    </>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>Tarot Spread Journal</DialogTitle>
          <DialogDescription>Create and journal tarot spreads with AI-powered readings</DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[90vh]">
          <div className="p-6 space-y-6">
            <DialogHeader>
              <DialogTitle className="text-2xl flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-primary" />
                Tarot Spreads & Journaling
              </DialogTitle>
            </DialogHeader>

            {/* Directions */}
            {showDirections && (
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-2">
                    <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="space-y-2 text-sm">
                      <p className="font-semibold">How to use Tarot Spreads:</p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>Choose your spread type below</li>
                        <li>Enter your question - be specific and open-ended</li>
                        <li>Click "Draw Cards" to receive your reading</li>
                        <li>Click any card to see its full meaning and interpretation</li>
                        <li>Generate an AI reading for deeper insights (uses 1 credit)</li>
                        <li>Journal your own interpretations and reflections</li>
                        <li>Save your reading to review later</li>
                      </ol>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Select Spread */}
            <div className="space-y-3">
              <h3 className="font-semibold">Choose a Spread</h3>
              <div className="grid md:grid-cols-3 gap-3">
                {SPREADS.map((spread, idx) => (
                  <Card 
                    key={idx}
                    className={`cursor-pointer transition-all ${
                      selectedSpread.name === spread.name 
                        ? 'ring-2 ring-primary' 
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => {
                      setSelectedSpread(spread);
                      setDrawnCards([]);
                      setShowDirections(true);
                    }}
                  >
                    <CardContent className="pt-4 space-y-2">
                      <div>
                        <h4 className="font-semibold mb-1">{spread.name}</h4>
                        <p className="text-xs text-muted-foreground mb-2">{spread.description}</p>
                        <Badge variant="secondary" className="mb-3">{spread.positions.length} cards</Badge>
                      </div>
                      
                      <div className="space-y-2 text-xs pt-2 border-t border-border/50">
                        <div>
                          <p className="font-semibold text-primary mb-1">Best for:</p>
                          <p className="text-muted-foreground leading-relaxed">{spread.bestFor}</p>
                        </div>
                        
                        <div>
                          <p className="font-semibold text-primary mb-1">Example questions:</p>
                          <ul className="space-y-1 text-muted-foreground">
                            {spread.goodQuestions.map((q, qIdx) => (
                              <li key={qIdx} className="leading-relaxed">• {q}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Question Input */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-primary">Your Question</label>
              <Input
                placeholder="What do I need to know about...? How can I...? What is the best path forward for...?"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="border-primary/20"
              />
              <p className="text-xs text-muted-foreground">
                Tip: Ask open-ended questions that invite guidance rather than yes/no questions
              </p>
            </div>

            {/* Draw Cards */}
            <div className="space-y-3">
              <div className="flex gap-2">
                <Button 
                  onClick={drawCards} 
                  className="flex-1 gap-2"
                  variant="outline"
                  disabled={!question.trim()}
                >
                  <Shuffle className="h-4 w-4" />
                  Draw Cards for {selectedSpread.name}
                </Button>
                
                {drawnCards.length > 0 && (
                  <Button
                    onClick={startFreshSpread}
                    variant="ghost"
                    size="icon"
                    title="Start a fresh spread"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {drawnCards.length > 0 && (
                <div className="space-y-4">
                  {selectedSpread.layout === 'celtic-cross' ? (
                    renderCelticCross()
                  ) : selectedSpread.name === 'Past-Present-Future' ? (
                    renderPastPresentFuture()
                  ) : selectedSpread.name === 'Relationship Spread' ? (
                    renderRelationshipSpread()
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {drawnCards.map((card, idx) => (
                        <div 
                          key={idx} 
                          className="cursor-pointer"
                          onClick={() => setSelectedCard(card)}
                        >
                          {renderCard(card, idx)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Journal Sections */}
            {drawnCards.length > 0 && (
              <div className="space-y-4">
                {/* Interpretation Section - Required First */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-primary flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Your Interpretation (Required)
                  </label>
                  <Textarea
                    placeholder="What story do these cards tell together? Trust your intuition - there are no wrong answers!"
                    value={interpretation}
                    onChange={(e) => setInterpretation(e.target.value)}
                    rows={4}
                    className="border-primary/20"
                  />
                  <p className="text-xs text-muted-foreground">
                    💡 Write your interpretation first - even a quick note helps develop your intuition!
                  </p>
                </div>

                {/* AI Reading Button */}
                <Button 
                  onClick={generateAiReading}
                  disabled={isGeneratingReading || !interpretation.trim()}
                  className="w-full gap-2 bg-gradient-to-r from-accent to-soft-accent hover:from-soft-accent hover:to-accent text-background"
                  variant="outline"
                >
                  <Sparkles className="h-4 w-4" />
                  {isGeneratingReading ? 'Tarot Teacher is Reading...' : !interpretation.trim() ? 'Share Your Interpretation First' : 'Get Tarot Teacher\'s Insights (1 Credit)'}
                </Button>

                {/* AI Reading Display */}
                {aiReading && (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-accent/10 to-primary/10 border-2 border-accent/30 rounded-lg overflow-hidden">
                      <div className="p-4 bg-gradient-to-r from-accent/20 to-primary/20 border-b border-accent/30">
                        <h3 className="font-bold text-xl text-primary flex items-center gap-2">
                          <Sparkles className="h-6 w-6" />
                          Tarot Teacher's Insights
                        </h3>
                      </div>
                      <ScrollArea className="max-h-[60vh]">
                        <div className="p-6 space-y-6">
                          {/* Parse and render the AI reading with better formatting */}
                          <div 
                            className="prose prose-sm max-w-none
                              prose-h2:text-xl prose-h2:mb-3 prose-h2:mt-6 prose-h2:pb-2 prose-h2:border-b prose-h2:border-primary/20
                              prose-h3:text-lg prose-h3:mb-2 prose-h3:mt-4 prose-h3:text-accent
                              prose-strong:text-primary prose-strong:font-semibold
                              prose-p:text-foreground prose-p:leading-relaxed prose-p:mb-3
                              prose-hr:border-accent/20 prose-hr:my-4"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                aiReading
                                  .replace(/## (.*?)(?=\n|$)/g, '<h2>$1</h2>')
                                  .replace(/### (.*?)(?=\n|$)/g, '<h3>$1</h3>')
                                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                  .replace(/---/g, '<hr />')
                                  .replace(/\n\n/g, '</p><p>')
                                  .replace(/^(.)/gm, '<p>$1')
                                  .replace(/(.)\n$/gm, '$1</p>'),
                                {
                                  ALLOWED_TAGS: ['h2', 'h3', 'strong', 'p', 'hr'],
                                  ALLOWED_ATTR: []
                                }
                              )
                            }}
                          />
                        </div>
                      </ScrollArea>
                    </div>
                    
                    <p className="text-xs text-center text-muted-foreground italic">
                      💡 Compare Tarot Teacher's insights with your own interpretation - what resonates? What surprises you?
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-primary">Personal Reflection</label>
                  <Textarea
                    placeholder="How does this reading relate to your current situation?"
                    value={reflection}
                    onChange={(e) => setReflection(e.target.value)}
                    rows={4}
                    className="border-primary/20"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-primary">Symbolic Insights</label>
                  <Textarea
                    placeholder="What symbols, patterns, or connections stand out?"
                    value={symbolicInsights}
                    onChange={(e) => setSymbolicInsights(e.target.value)}
                    rows={3}
                    className="border-primary/20"
                  />
                </div>

                <Button 
                  onClick={saveJournalEntry}
                  className="w-full gap-2 bg-gradient-to-r from-primary to-accent hover:from-primary-light hover:to-accent text-background"
                >
                  <BookOpen className="h-4 w-4" />
                  Save Journal Entry
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>

      {/* Card Detail Dialog */}
      {selectedCard && (
        <TarotCardDetail
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
        />
      )}
    </Dialog>
  );
}